import clsx from 'clsx';
import { useState, RefObject, useEffect } from 'react';
import { miniStreamingStyle } from './mini-streaming.style';
import { Dropdown } from '@nf/ui';
import type { DropdownEvent, DropdownOption } from '@nf/ui';
import { Button } from '@nf/ui';
import { css } from 'styled-system/css';
import { Icon } from '@/components/icons/svg-icons';
import { useTargetScrollableContext } from '@/components/containers/target-scrollable-container';
import { miniStreamingService } from '@/services/mini-streaming.service';
import { motion, useScroll } from 'framer-motion';
import smoothscroll from 'smoothscroll-polyfill';
import { SportMatchModel } from '@nf/types/odds';
import { ChannelModel, streamingService } from '@/services/streaming.service';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { useAccountInfo } from '@/hooks/query/account';
import { useTranslations } from 'next-intl';
import { useStreamingStore } from '@/store/streaming';
import { useAtomValue } from 'jotai';
import { oddsPageAtom } from '@/atoms/odds-page';
import { getHomeTeamName, getTeamName } from '@/utils/odds-utils';
import { MatchTimer } from '@/features/sportsbook-aside/components/match-item/match-timer';
import { useInView } from 'react-intersection-observer';
import { useAppearanceStore } from '@/store/appearance';
import { setKafkaNormal, setKafkaStreaming } from '@/hooks/query/use-kafka-log';
import { ViewEnum } from '@/types';
import { KafkaNote, StreamingKafkaNote } from '@nf/utils-common/constants';
import { getKafkaStreamerType } from '@/utils/common-util';;
import { useParams } from 'next/navigation';
import {
	InHouseStreamingUrlWithSecretResponse,
	useGetInHouseStreamingUrlWithSecret
} from '@/hooks/query/use-streaming-data';
import { StreamingControl } from '@/features/sportsbook-aside/components/streaming-item/streaming-control';
import { useSlmtGv } from '@/hooks/query/use-slmt-gv';
import pluginService from '@/services/discover-plugin.service';

interface MiniStreamingProps {
	match: SportMatchModel;
	parrentRef: RefObject<HTMLDivElement>;
};

const useChannelList = (match: SportMatchModel): ChannelModel[] => {
	const t = useTranslations('General');
	const { SiteConfigsData } = useGetSiteConfigs();
	const { user } = useAccountInfo();
	const [channelList, setChannelList] = useState<ChannelModel[]>(streamingService.getChannelList([t('lbl_Streamer'), t.raw('lbl_Source')], match, SiteConfigsData, user));
	const [inHouseStreamingIndex] = useState<number>(channelList.findIndex(channelModel => channelModel.value === '13'));
	const { refetch } = useGetInHouseStreamingUrlWithSecret(
		inHouseStreamingIndex === -1 ? '' : channelList[inHouseStreamingIndex].sid,
		user?.ApiBackendUrl || '',
		user?.AccessToken || ''
	);

	useEffect(() => {
		if (inHouseStreamingIndex !== -1) {
			refetch().then((response) => {
				const { value, sid, fixing, label, type, siteId } = channelList[inHouseStreamingIndex];
				const inHouseStreamingUrlGroups: InHouseStreamingUrlWithSecretResponse[] = response.data?.data?.Data || [];
				const inHouseOtherSource: ChannelModel[] = [] as ChannelModel[];
				for (let index = 1; index < inHouseStreamingUrlGroups.length; index++) {
					inHouseOtherSource.push({
						label: `${label}-${index}`,
						value: `${value}-${index}`,
						sid: sid,
						fixing: fixing,
						type: type,
						siteId: siteId,
					});
				}

				const newChannelList = inHouseOtherSource.length > 0
					? channelList.length === 1
						? [...channelList, ...inHouseOtherSource]
						: [
							...channelList.slice(0, inHouseStreamingIndex + 1),
							...inHouseOtherSource,
							...channelList.slice(inHouseStreamingIndex + 1)
						]
					: channelList;

				setChannelList(newChannelList);
			})
		}
	}, []);

	return channelList;
}

export const MiniStreaming = ({
	match,
	parrentRef
}: MiniStreamingProps) => {
	const scrollableRef = useTargetScrollableContext();
	const t = useTranslations('General');
	const { SiteConfigsData } = useGetSiteConfigs();
	const { user } = useAccountInfo();
	const { sport } = useParams();
	const { hasSlmtGv } = useSlmtGv({ match, sport });
	const channelList = useChannelList(match);
	const oddsData = useAtomValue(oddsPageAtom);
	const [
		streamingType,
		setIsEsport,
		setIsMiniStreaming,
		setStreamingMatchId,
		setStreamingType,
		setStreamingSrc,
		setChannelList,
		setStreamingUrl,
		setIsLoading
	] = useStreamingStore(state => [
		state.streamingType,
		state.setIsEsport,
		state.setIsMiniStreaming,
		state.setStreamingMatchId,
		state.setStreamingType,
		state.setStreamingSrc,
		state.setChannelList,
		state.setStreamingUrl,
		state.setIsLoading
	]);
	const { MatchId, GameID, HLS, T1V, T2V, Tsta } = match;
	const { hasVideo, hasLSports, hasStreamer } = streamingService.getVideoOptions(match, false, SiteConfigsData, user);
	const { scrollY } = useScroll({ container: scrollableRef });
	const [isBase, setIsBase] = useState(true);
	const [channel, setChannel] = useState(channelList[0].value);
	const [isMini, setIsMini] = useState(false);
	const [isChannelListOpen, setIsChannelListOpen] = useState(false);
	const [isScolledTop, setIsScolledTop] = useState<boolean>(false);
	const homeTeamName = getHomeTeamName(match.TeamId1, match.Neu, t('Neutral_Team'), oddsData?.teamNameMap);
	const awayTeamName = getTeamName(match.TeamId2, oddsData?.teamNameMap);
	const { ref, inView } = useInView({
		/* Optional options */
		threshold: 0,
	});
	const isFloat = !inView;
	const isForceMini = (isMini || isFloat);
	const view = useAppearanceStore(state => state.view);

	const isStreamingPlugin = SiteConfigsData?.EnableStreamingPlugin && pluginService.checkIsSupportSource(channelList, match);
	const streamingPluginType = (user?.ActStatus === 2 && isStreamingPlugin) ? 
		streamingService.checkPluginType(streamingType, SiteConfigsData?.EnableChatroom || false, hasStreamer, true) : 0;

	useEffect(() => {
		setIsMiniStreaming(true);
		setStreamingMatchId(MatchId);
		setStreamingType(hasVideo ? 'Streaming' : '');
		setStreamingSrc(channel);
		setChannelList(channelList);
		setIsLoading(true);

		if (match.GameID === 43 && streamingService.EsportStreamingSrc.includes(channel)) {
			setIsEsport(true);
		}

		return () => {
			setIsMiniStreaming(false);
			setStreamingMatchId(0);
			setStreamingUrl('');
			setStreamingType('');
			setStreamingSrc('');
			setChannelList([]);
			setIsLoading(false);
			setIsEsport(false);
		}
	}, []);

	useEffect(() => {
		setChannelList(channelList);
	}, [channelList]);

	useEffect(() => {
		const mediaQuery = window.matchMedia('(min-width: 750px)');

		const handleMediaChange = () => {
			setIsBase(window.innerWidth < 750);
		};

		mediaQuery.addEventListener('change', handleMediaChange);
		return () => {
			mediaQuery.removeEventListener('change', handleMediaChange);
		};
	}, []);

	const createValueLabel = () => (
		<Icon name="channel" />
	);

	const createCustomOption = (option: DropdownOption) => (
		<div className={css(miniStreamingStyle.option, option.value === channel && miniStreamingStyle.optionActive)}>
			{option.label}
			<Icon name="ticket-success-line" />
		</div>
	);

	const toggleMini = () => {
		setIsMini(!isForceMini);
	};

	const handleChange = (e: DropdownEvent) => {
		const optionValue = e.option.value;
		const streamerKafkaData = {
			note: StreamingKafkaNote.MiniStreaming,
			type: optionValue === '11' ? 3 : (optionValue === '19' ? 4 : 5),
			action: 1,
			matchId: 0,
			streamer_type: getKafkaStreamerType(0)
		};
		setKafkaStreaming(streamerKafkaData);
		setChannel(optionValue);
		setStreamingSrc(optionValue);
		setStreamingUrl('');
		setIsLoading(true);
	};

	const scrollToMatch = () => {
		smoothscroll.polyfill();
		const headerOffset = isBase ? -72 : -114; //height of product list and sport menu
		const refTop = parrentRef.current?.getBoundingClientRect().top ?? 0;
		const top = refTop + headerOffset;

		scrollTo({ top: top + window.scrollY, behavior: 'smooth' });
		scrollableRef.current?.scrollTo({ top: top + scrollY.get(), behavior: 'smooth' });
	}

	const closeMiniStreaming = () => {
		const streamerKafkaData = {
			note: StreamingKafkaNote.MiniStreaming,
			type: 2,
			action: 2,
			matchId: 0,
			streamer_type: getKafkaStreamerType(0)
		};
		setKafkaStreaming(streamerKafkaData);
		miniStreamingService.setPlayingStreamingMatchId(-1);
	}

	useEffect(() => {
		const subscription = miniStreamingService.isScrolledNearTop.subscribe((status: boolean) => {
			setIsScolledTop(status)
		});
		return () => subscription.unsubscribe()
	}, [])
	
	return (
		<motion.div ref={ref}>
			<div
				className={clsx(
					'mini-streaming',
					css(miniStreamingStyle.root && miniStreamingStyle.rootShow),
					isForceMini && !isStreamingPlugin && css(miniStreamingStyle.rootMini),
					isForceMini && isStreamingPlugin && css(miniStreamingStyle.rootMiniPugin),
					isFloat && css(miniStreamingStyle.rootFloat),
				)}
				data-is-top={isScolledTop}
			>
				<div className={css(
					miniStreamingStyle.content,
					isForceMini && miniStreamingStyle.contentMini,
					isFloat && miniStreamingStyle.contentFloat,
				)}
				>
					<div className={css(miniStreamingStyle.video, isForceMini && miniStreamingStyle.videoMini)}>
						<StreamingControl
							match={match}
							device={'mobile'}
							isStreamingPlugin={isStreamingPlugin}
							streamingPluginType={streamingPluginType}
						/>
					</div>
					<div className={css(miniStreamingStyle.info, isForceMini && miniStreamingStyle.infoMini)}>
						<div className={css(miniStreamingStyle.status, isForceMini && miniStreamingStyle.statusMini)}>
							{
								isForceMini &&
									HLS === 0 &&
									![50, 55, 56, 57, 58, 59, 60, 61, 62, 63, 64, 65, 66, 99].includes(GameID) &&
									!([28, 47].includes(GameID) && Tsta)
									? (
										<>
											{
												GameID === 44 ?
													<div className={miniStreamingStyle.score}>{T1V ? (t('lbl_round') + T1V) : null}</div> :
													<div className={miniStreamingStyle.score}>{`${T1V} - ${T2V}`}</div>
											}
											<div className={css(miniStreamingStyle.time, isForceMini && miniStreamingStyle.timeMini)}>
												<MatchTimer match={match} />
											</div>
										</>
									) : null
							}
							<div className={css(miniStreamingStyle.videoButtons, isForceMini && miniStreamingStyle.videoButtonsMini)}>
								{/* 播放中：data-active={true} 與兄弟 Button 互斥 */}
								{
									hasVideo && hasLSports ?
										<Button
											variant="icon"
											data-active={streamingType === 'Streaming'}
											className={css(miniStreamingStyle.videoButton, isForceMini && miniStreamingStyle.videoButtonMini)}
											onClick={() => {
												if (view === ViewEnum.mars) {
													const kafkaData = {
														note: KafkaNote.MarsV1,
														type: 6,
														action: 9
													}
													setKafkaNormal(kafkaData);
												}
												setStreamingType('Streaming')
											}}
										>
											<i className="symbol symbol--video" />
										</Button> : null
								}
								{
									hasSlmtGv ?
										<Button
											variant="icon"
											data-active={streamingType === 'SlmtGV'}
											className={css(miniStreamingStyle.videoButton, isForceMini && miniStreamingStyle.videoButtonMini)}
											onClick={() => {
												if (view === ViewEnum.mars) {
													const kafkaData = {
														note: KafkaNote.MarsV1,
														type: 6,
														action: 10
													}
													setKafkaNormal(kafkaData);
												}
												setStreamingType('SlmtGV')
											}}
										>
											<i className="symbol symbol--gv" />
										</Button> : null
								}
							</div>
							<div className={css(miniStreamingStyle.feature, isForceMini && miniStreamingStyle.featureMini)}>
								{
									streamingType === 'Streaming' && channelList.length > 1 && !isStreamingPlugin ?
										<Dropdown
											variant="right"
											className={css(
												miniStreamingStyle.channel,
												isForceMini && miniStreamingStyle.channelMini,
												isChannelListOpen && miniStreamingStyle.channelActive
											)}
											value={channel}
											options={channelList}
											iconTemplate={() => null}
											valueTemplate={createValueLabel}
											optionTemplate={createCustomOption}
											onChange={handleChange}
											onOpenToggled={() => {
												if (view === ViewEnum.mars) {
													const kafkaData = {
														note: KafkaNote.MarsV1,
														type: 6,
														action: 11
													}
													setKafkaNormal(kafkaData);
												}
												setIsChannelListOpen(!isChannelListOpen)
											}}
										/> : null
								}
								{isFloat ? (
									<Button
										variant="icon"
										onClick={() => {
											if (view === ViewEnum.mars) {
												const kafkaData = {
													note: KafkaNote.MarsV1,
													type: 6,
													action: 14
												}
												setKafkaNormal(kafkaData);
											}
											scrollToMatch()
										}}
									>
										<Icon name="return" />
									</Button>
								) : (
									<Button variant="icon" onClick={() => {
										if (view === ViewEnum.mars) {
											const kafkaData = {
												note: KafkaNote.MarsV1,
												type: 6,
												action: isForceMini ? 13 : 12
											}
											setKafkaNormal(kafkaData);
										}
										toggleMini()
									}}
									>
										<Icon name={isForceMini ? 'full-screen-on' : 'full-screen-off'} />
									</Button>
								)}
								<Button variant="icon" onClick={closeMiniStreaming}>
									<Icon name="close-big" />
								</Button>
							</div>
						</div>
						<div className={css(miniStreamingStyle.match, isForceMini && miniStreamingStyle.matchMini)}>
							<div className={css(miniStreamingStyle.team, miniStreamingStyle.teamMini)}>
								<span className={clsx('multi-line-ellipsis', miniStreamingStyle.teamName)}>{homeTeamName}</span>
							</div>
							{isForceMini ? null : <div className={miniStreamingStyle.vs}>-vs-</div>}
							<div className={css(miniStreamingStyle.team, miniStreamingStyle.teamMini)}>
								<span className={clsx('multi-line-ellipsis', miniStreamingStyle.teamName)}>{awayTeamName}</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</motion.div>
	);
};